import _ from 'underscore';
import { assertIsDefined } from '@bingads-webui-campaign-react/debug-assert';
import { WithTreeViewData } from '@bingads-webui-grid/well-known';
import { ListingGroupGridItemData } from '../interface';
import { Model$Listing, Model$ListingCondition, Model$ListingGroup } from '../ApiType';
import { GetOdataAdsAPIParams, getFullUrlOfAdsOdata } from './adsOdata';

const NonSplittableConditions = ['CategoryL1', 'CategoryL2', 'CategoryL3', 'CategoryL4', 'CategoryL5'];

export type GetListingGroupConditionsParams = GetOdataAdsAPIParams<Model$ListingGroup>;

interface GetListingGroupConditionsResponseBody {
  value: Model$ListingCondition[];
}

export function getListingGroupConditionsRequest(baseUrl: string, params: GetListingGroupConditionsParams) {
  const path = 'ProductListingGroups/Default.GetProductListingGroupConditions';
  const finalUrl = getFullUrlOfAdsOdata(baseUrl, path, params);
  return [finalUrl, {
    method: 'GET',
  }];
}

export async function getListingGroupConditionsResponseHandler(responsePromise: Promise<Response>) {
  const response = await responsePromise;
  const responseBody: GetListingGroupConditionsResponseBody = await response.json();
  return responseBody;
}

// If we have used the dimension, it should not be used anymore in its subtree
//   @param `pathsToParent` from root to current node, like [rootInfo, path1Info, currentNodeDimensionInfo]
function filterOutUsedDimensions(listingConditions: Model$ListingCondition[], pathsToParent: Model$Listing[]) {
  return listingConditions.filter(c =>
    pathsToParent.every(p => p.Operand !== c.Operand));
}

// Some dimension are only avaliable after some other dimension is chosen
// Note: root is filtered out in this step
//   @param `pathsToParent` from root to current node, like [rootInfo, path1Info, currentNodeDimensionInfo]
function filterReachableDimensions(listingConditions: Model$ListingCondition[], pathsToParent: Model$Listing[]): Model$ListingCondition[] {
  // Current impl is not effective, but easy to understand :)
  const reservedIds = new Set<number>();
  pathsToParent.forEach((path) => {
    const currentOperand = path.Operand;
    const currentId = listingConditions.find(c =>
      (c.Operand === 'All' && currentOperand === '') ||
      c.Operand === currentOperand)?.Id;
    assertIsDefined(currentId);
    listingConditions.forEach((c) => {
      if (c.ParentId === currentId) {
        reservedIds.add(c.Id);
      }
    });
  });
  return listingConditions.filter(c => reservedIds.has(c.Id));
}

// There are some dimensions that everything else node cannot be split on
function filterNonSplittableDimensions(listingConditions: Model$ListingCondition[], item:(WithTreeViewData<ListingGroupGridItemData>)): Model$ListingCondition[] {
  assertIsDefined(item);

  let isFromEverythingElse = false;
  if (item.Condition?.Operand !== '' && item.Condition?.Attribute === '') {
    isFromEverythingElse = true;
  }

  _.each(item.ParentConditions || [], (condition) => {
    if (condition?.Operand !== '' && condition?.Attribute === '') {
      isFromEverythingElse = true;
    }
  });

  assertIsDefined(item.ParentConditions);
  assertIsDefined(item.Condition);

  const parentConditionOperands = _.pluck(item.ParentConditions, 'Operand');
  const allItemOperands = [item.Condition.Operand, ...parentConditionOperands];

  if (_.intersection(NonSplittableConditions, allItemOperands) && isFromEverythingElse) {
    return _.filter(listingConditions, condition => !_.contains(NonSplittableConditions, condition.Operand));
  }

  return listingConditions;
}

// Do we need to filter the country by current Campaign? In backend, we get current campaign by use something like `this.contextProvider.CurrentCampaign?.CampaignSettings[0]?.SalesCountry;`
// function filterCountry() { }

// All data is returned now, however, user should only see part of them
//     - user should not see any dimension they have used
//     - user should not see the root dimension
//     - user should not see a dimension if its partent is not reached
// In conclusion, a valid node is valid if it is the direct son of a `pathsToParent` and it is not in `pathsToParent`
//   @param `pathsToParent` from root to current node, like [rootInfo, path1Info, currentNodeDimensionInfo]
export function getValidListingConditions(response: GetListingGroupConditionsResponseBody, pathsToParent: Model$Listing[], item: WithTreeViewData<ListingGroupGridItemData>) {
  let conditions = response.value;
  conditions = filterReachableDimensions(conditions, pathsToParent);
  conditions = filterOutUsedDimensions(conditions, pathsToParent);
  conditions = filterNonSplittableDimensions(conditions, item);
  return conditions;
}

export function getValidListingConditionsForWithoutPreselectedTreeNode(response: GetListingGroupConditionsResponseBody, pathsToParent: Model$Listing[]) {
  let conditions = response.value;
  conditions = filterReachableDimensions(conditions, pathsToParent);
  conditions = filterOutUsedDimensions(conditions, pathsToParent);
  return conditions;
}
